<script lang="ts" setup>
import { computed, onMounted, ref, VNode } from "vue";
import { VueTelInput } from "vue-tel-input";
import "vue-tel-input/vue-tel-input.css";

import { InputVariant } from "@/types";

interface Props {
  label?: string;
  labelIcon?: string | object | (() => VNode) | null;
  variant?: InputVariant;
  modelValue?: string | number;
  testId?: string;
  disabled?: boolean;
  required?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  label: "",
  variant: "primary",
  modelValue: "",
  testId: "phone-input",
  disabled: false,
  required: false
});

const emit = defineEmits<(e: "update:modelValue", value: string | number) => void>();

const phone = computed({
  get: () => props.modelValue,
  set: (val: string | number) => emit("update:modelValue", val)
});
const variantClass = computed(() => props.variant ?? 'primary');
const backgroundStyle = computed(() => ({
  background:
    phone.value && ["primary", "admin"].includes(props.variant)
      ? "rgba(28, 86, 145, 0.05)"
      : "none",
}));

const telInputRef = ref<InstanceType<typeof VueTelInput> | null>(null);

onMounted(() => {
  // Wait for DOM to be ready and try to find the internal input element
  const input = telInputRef.value?.$el?.querySelector('input');
  if (input) {
    input.dataset.testid = props.testId;
  }
});
</script>

<template>
  <div :class="['tel-input-wrapper w-100', variantClass]">
    <label
      v-if="label"
      class="input-label d-flex align-items-center"
      :for="testId"
    >
      {{ label }}
      <span v-if="required">&ast;</span>
      <component v-if="labelIcon" :is="labelIcon" class="ms-1" />
    </label>
    <vue-tel-input
      ref="telInputRef"
      v-model="phone"
      mode="international"
      :style="{ ...backgroundStyle }"
      :disabled="disabled"
      :dropdownOptions="{
        showFlags: true,
        showDialCodeInList: true,
        width: '240px',
      }"
      :inputOptions="{
        id: testId,
        placeholder: 'Enter phone number',
        maxlength: 17,
        styleClasses: 'vti-phone-input',
        showDialCode: true
      }"
      :defaultCountry="234"
      :validCharactersOnly="true"
      v-bind="$attrs"
      ></vue-tel-input>
  </div>
</template>

<style scoped>
.tel-input-wrapper .input-label {
  margin-bottom: 0.25rem;
  font-size: var(--font-xs);
  font-weight: 400;
  text-transform: capitalize;
}

.tel-input-wrapper .vti__dropdown {
  background-color: transparent;
  border: 1px solid var(--ch-black);
}

.tel-input-wrapper .vue-tel-input {
  padding: 0.25rem 0.5rem;
  width: 100%;
  height: 2.5rem;
  max-height: 2.5rem;
  font-size: var(--font-sm);
  font-weight: 400;
}

.tel-input-wrapper .vue-tel-input:focus-within {
  box-shadow: 0 0 0 2px color-mix(in srgb, currentColor 10%, transparent);
  outline: none;
}

.vue-tel-input ul.vti__dropdown-list {
  max-width: 17.5rem;
}

:deep(input.vti-phone-input::placeholder) {
  color: rgba(28, 86, 145, 0.2);
  font-size: var(--font-xs);
}

:deep(input.vti-phone-input) {
  background: rgba(28, 86, 145, 0.005);
  border-radius: 0 6px 6px 0;
}

:deep(
  input.vti-phone-input:-webkit-autofill,
  input.vti-phone-input:-webkit-autofill:hover,
  input.vti-phone-input:-webkit-autofill:focus,
) {
  -webkit-box-shadow: 0 0 0 1000px rgba(28, 86, 145, 0.005) inset !important;
  transition: background-color 5000s ease-in-out 0s !important;
}

@media screen and (min-width: 1024px) {
  .tel-input-wrapper .input-label {
    font-size: var(--font-sm);
  }
}

/* Variant Styles */

/* Primary Variant */
.tel-input-wrapper.primary .input-label {
  color: var(--ch-black);
}

.tel-input-wrapper.primary .vue-tel-input {
  border: 1px solid var(--ch-blue-100);
  border-radius: 6px;
}

.tel-input-wrapper.primary .vue-tel-input:focus-within {
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--ch-blue-100) 15%, transparent);
}

.tel-input-wrapper.primary :deep(input.vti-phone-input) {
  color: var(--ch-black);
  border-left: 1px solid var(--ch-blue-100);
}

/* AdminV1 Variant */
.tel-input-wrapper.admin .input-label {
  color: var(--ch-text-head);
}

.tel-input-wrapper.admin .vue-tel-input {
  border: 1px solid var(--ch-input);
  border-radius: 5px;
}

.tel-input-wrapper.admin :deep(input.vti-phone-input) {
  color: var(--ch-text-head);
  border-left: 1px solid var(--ch-input);
}

/* Grey Variant */
.tel-input-wrapper.grey .input-label {
  color: var(--ch-black);
}

.tel-input-wrapper.grey .vue-tel-input {
  border: 1px solid var(--ch-border-grey-30);
  border-radius: 6px;
}

.tel-input-wrapper.grey :deep(input.vti-phone-input::placeholder) {
  color: var(--ch-border-grey);
}

.tel-input-wrapper.grey .vue-tel-input:focus-within {
  border: 2px solid var(--ch-blue-100);
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--ch-blue-100) 10%, transparent);
}

.tel-input-wrapper.grey :deep(input.vti-phone-input) {
  color: var(--ch-black);
  border-left: 1px solid var(--ch-border-grey-30);
}
</style>
